import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Package, ChevronRight, ChevronLeft, Building2, ArrowLeft, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { getCategories } from '../services/api';

const Products = () => {
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const location = useLocation();
  const carouselRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await getCategories();
        setProductCategories(data);
        
        // Check if a category was pre-selected from navigation
        if (location.state?.selectedCategoryId) {
          const preSelectedCategory = data.find(
            cat => cat.id === location.state.selectedCategoryId
          );
          if (preSelectedCategory) {
            setSelectedCategory(preSelectedCategory);
          }
          // Clear the state to prevent re-selecting on refresh
          window.history.replaceState({}, document.title);
        }
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [location.state]);

  // Reset carousel when category changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [selectedCategory]);

  // Get carousel images from category, brands, and products
  const getCarouselImages = () => {
    if (!selectedCategory) return [];
    
    const images = [];
    
    // Add category image first
    if (selectedCategory.image) {
      images.push({
        url: selectedCategory.image,
        title: selectedCategory.name,
        subtitle: selectedCategory.description || 'Premium quality products'
      });
    }
    
    // Add brand logos (if they have logos)
    selectedCategory.brands?.forEach(brand => {
      if (brand.logo || brand.logo_url) {
        images.push({
          url: brand.logo || brand.logo_url,
          title: brand.name,
          subtitle: `${brand.products?.length || 0} products`,
          isBrandLogo: true
        });
      }
    });
    
    // Add product images
    selectedCategory.brands?.forEach(brand => {
      brand.products?.forEach(product => {
        if (product.image) {
          images.push({
            url: product.image,
            title: product.name,
            subtitle: brand.name
          });
        }
      });
    });
    
    // If no images, add category image as fallback
    if (images.length === 0) {
      images.push({
        url: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=800',
        title: selectedCategory.name,
        subtitle: 'Explore our products'
      });
    }
    
    return images;
  };

  const carouselImages = selectedCategory ? getCarouselImages() : [];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (!selectedCategory || carouselImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [selectedCategory, carouselImages.length]);

  // Filter categories based on search
  const filteredCategories = productCategories.filter(category => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return category.name.toLowerCase().includes(search) ||
           (category.description && category.description.toLowerCase().includes(search)) ||
           category.brands.some(brand => 
             brand.name.toLowerCase().includes(search) ||
             brand.products.some(product => 
               product.name.toLowerCase().includes(search)
             )
           );
  });

  const getTotalBrands = (category) => category.brands?.length || 0;
  const getTotalProducts = (category) => 
    category.brands?.reduce((sum, brand) => sum + (brand.products?.length || 0), 0) || 0;

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSearchTerm('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSearchTerm('');
  };

  // Filter brands within selected category based on search
  const getFilteredBrands = () => {
    if (!selectedCategory) return [];
    if (!searchTerm) return selectedCategory.brands || [];
    
    const search = searchTerm.toLowerCase();
    return selectedCategory.brands.filter(brand =>
      brand.name.toLowerCase().includes(search) ||
      brand.products.some(product => product.name.toLowerCase().includes(search))
    );
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Different for category view vs grid view */}
      {!selectedCategory ? (
        <section className="py-16 section-teal text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Our Products</h1>
            <div className="decorative-line max-w-md"></div>
            <p className="text-lg max-w-3xl mx-auto mt-4 text-gray-100">
              Explore our comprehensive range of premium quality products from trusted brands
            </p>
          </div>
        </section>
      ) : (
        /* Category Carousel/Banner */
        <section className="relative h-[300px] sm:h-[400px] overflow-hidden" ref={carouselRef}>
          {/* Carousel Slides */}
          <div className="relative w-full h-full">
            {carouselImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {image.isBrandLogo ? (
                  /* Brand Logo Display */
                  <div className="w-full h-full bg-gradient-to-br from-[var(--primary-teal)] to-[var(--primary-teal-dark)] flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-4 rounded-full bg-white shadow-xl flex items-center justify-center p-4">
                        <img 
                          src={image.url} 
                          alt={image.title}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">{image.title}</h2>
                      <p className="text-lg text-gray-200">{image.subtitle}</p>
                    </div>
                  </div>
                ) : (
                  /* Regular Image Display */
                  <>
                    <img 
                      src={image.url} 
                      alt={image.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=800';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 text-white">
                      <h2 className="text-3xl sm:text-5xl font-bold mb-2">{image.title}</h2>
                      <p className="text-lg sm:text-xl text-gray-200">{image.subtitle}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Carousel Controls */}
          {carouselImages.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-all z-10"
                data-testid="carousel-prev"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-all z-10"
                data-testid="carousel-next"
              >
                <ChevronRight size={28} />
              </button>

              {/* Carousel Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {carouselImages.slice(0, 10).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-white w-8' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                  />
                ))}
                {carouselImages.length > 10 && (
                  <span className="text-white text-xs self-center ml-2">
                    +{carouselImages.length - 10} more
                  </span>
                )}
              </div>
            </>
          )}

          {/* Back Button Overlay */}
          <div className="absolute top-4 left-4 z-10">
            <Button 
              variant="outline" 
              onClick={handleBackToCategories}
              className="gap-2 bg-white/90 hover:bg-white border-0 shadow-lg"
              data-testid="back-to-categories-btn"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">All Categories</span>
            </Button>
          </div>

          {/* Category Info Overlay */}
          <div className="absolute top-4 right-4 z-10 bg-white/90 rounded-lg px-4 py-2 shadow-lg">
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1" style={{ color: 'var(--primary-teal)' }}>
                <Building2 size={16} />
                <strong>{getTotalBrands(selectedCategory)}</strong> Brands
              </span>
              <span className="flex items-center gap-1" style={{ color: 'var(--gold-accent)' }}>
                <Package size={16} />
                <strong>{getTotalProducts(selectedCategory)}</strong> Products
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Search Section */}
      <section className="py-8 section-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-4">
              {selectedCategory && (
                <div className="text-lg font-semibold hidden sm:block" style={{ color: 'var(--primary-teal)' }}>
                  {selectedCategory.name}
                </div>
              )}
              <div className="flex-1 relative">
                <Search 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2" 
                  size={20}
                  style={{ color: 'var(--text-light)' }}
                />
                <Input
                  type="text"
                  placeholder={selectedCategory ? "Search brands or products..." : "Search categories..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-6"
                  data-testid="product-search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 section-cream pattern-overlay">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <Package size={64} className="mx-auto mb-4 animate-pulse" style={{ color: 'var(--primary-teal)' }} />
              <h3 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-medium)' }}>
                Loading products...
              </h3>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <Package size={64} className="mx-auto mb-4" style={{ color: 'var(--text-light)' }} />
              <h3 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-medium)' }}>
                Error loading products
              </h3>
              <p style={{ color: 'var(--text-light)' }}>{error}</p>
            </div>
          ) : !selectedCategory ? (
            /* Categories Grid View */
            <>
              <div className="mb-8 text-center">
                <p className="text-lg" style={{ color: 'var(--text-medium)' }}>
                  {filteredCategories.length} Categories Available
                </p>
              </div>

              {filteredCategories.length === 0 ? (
                <div className="text-center py-20">
                  <Package size={64} className="mx-auto mb-4" style={{ color: 'var(--text-light)' }} />
                  <h3 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-medium)' }}>
                    No categories found
                  </h3>
                  <p style={{ color: 'var(--text-light)' }}>
                    Try adjusting your search
                  </p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredCategories.map((category) => (
                    <Card 
                      key={category.id} 
                      className="overflow-hidden border-0 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                      onClick={() => handleCategoryClick(category)}
                      data-testid={`category-card-${category.id}`}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={category.image} 
                          alt={category.name}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                          <h3 className="text-xl font-bold mb-1">{category.name}</h3>
                          <div className="flex gap-3 text-sm opacity-90">
                            <span className="flex items-center gap-1">
                              <Building2 size={14} />
                              {getTotalBrands(category)} Brands
                            </span>
                            <span className="flex items-center gap-1">
                              <Package size={14} />
                              {getTotalProducts(category)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <p className="text-sm line-clamp-2" style={{ color: 'var(--text-medium)' }}>
                          {category.description || 'Explore premium products from top brands'}
                        </p>
                        <div className="mt-3 flex items-center justify-end text-sm font-medium" style={{ color: 'var(--primary-teal)' }}>
                          View Products <ChevronRight size={16} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Category Detail View - Brands & Products */
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <p className="text-lg" style={{ color: 'var(--text-medium)' }}>
                    {getFilteredBrands().length} Brands in {selectedCategory.name}
                  </p>
                </div>
              </div>

              {getFilteredBrands().length === 0 ? (
                <div className="text-center py-20">
                  <Package size={64} className="mx-auto mb-4" style={{ color: 'var(--text-light)' }} />
                  <h3 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-medium)' }}>
                    No brands found
                  </h3>
                  <p style={{ color: 'var(--text-light)' }}>
                    Try adjusting your search
                  </p>
                </div>
              ) : (
                <Card className="border-0 shadow-xl overflow-hidden">
                  <div className="p-6">
                    <Accordion type="single" collapsible className="w-full">
                      {getFilteredBrands().map((brand) => (
                        <AccordionItem key={brand.id} value={`brand-${brand.id}`} className="border-b last:border-0">
                          <AccordionTrigger className="hover:no-underline py-4">
                            <div className="flex items-center gap-4">
                              {(brand.logo_url || brand.logo) ? (
                                <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-gray-200 flex items-center justify-center shrink-0">
                                  <img 
                                    src={brand.logo_url || brand.logo} 
                                    alt={brand.name}
                                    className="w-10 h-10 object-contain"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `<span class="text-white font-bold text-lg" style="background-color: var(--gold-accent); width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; border-radius: 50%;">${brand.name.charAt(0)}</span>`;
                                    }}
                                  />
                                </div>
                              ) : (
                                <div 
                                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shrink-0"
                                  style={{ backgroundColor: 'var(--gold-accent)' }}
                                >
                                  {brand.name.charAt(0)}
                                </div>
                              )}
                              <div className="text-left">
                                <h3 className="text-xl font-bold" style={{ color: 'var(--primary-teal)' }}>
                                  {brand.name}
                                </h3>
                                <p className="text-sm" style={{ color: 'var(--text-light)' }}>
                                  {brand.products?.length || 0} products available
                                </p>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-4 pb-2">
                              {brand.products?.map((product, idx) => (
                                <div 
                                  key={product.id || idx}
                                  className="p-4 rounded-xl border-2 transition-all hover:shadow-lg bg-white"
                                  style={{ borderColor: 'var(--gold-accent)' }}
                                >
                                  {product.image && (
                                    <div className="w-full h-32 mb-3 rounded-lg overflow-hidden bg-gray-100">
                                      <img 
                                        src={product.image} 
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => e.target.parentElement.style.display = 'none'}
                                      />
                                    </div>
                                  )}
                                  <h4 className="font-semibold mb-2" style={{ color: 'var(--text-dark)' }}>
                                    {product.name}
                                  </h4>
                                  {product.sizes && product.sizes.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {product.sizes.map((size, sizeIdx) => (
                                        <span 
                                          key={sizeIdx}
                                          className="text-xs px-2 py-1 rounded-full"
                                          style={{
                                            backgroundColor: 'var(--cream-bg)',
                                            color: 'var(--text-medium)'
                                          }}
                                        >
                                          {size}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </section>

      {/* Product Features - Only show on categories view */}
      {!selectedCategory && (
        <section className="py-16 section-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--primary-teal)' }}>
                Quality You Can Trust
              </h2>
              <div className="decorative-line max-w-md"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="card-hover text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <div 
                    className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--gold-accent)' }}
                  >
                    <Package size={28} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--primary-teal)' }}>
                    Premium Brands
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-medium)' }}>
                    Products from trusted brands meeting international standards
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <div 
                    className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--primary-teal)' }}
                  >
                    <Filter size={28} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--primary-teal)' }}>
                    Authentic Products
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-medium)' }}>
                    Genuine Indian products with traditional quality
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <div 
                    className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--gold-accent)' }}
                  >
                    <Building2 size={28} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--primary-teal)' }}>
                    Multiple Sizes
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-medium)' }}>
                    Flexible packaging options for your market needs
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 section-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-2xl p-8 sm:p-12 text-center text-white"
            style={{
              background: 'linear-gradient(135deg, var(--primary-teal) 0%, var(--primary-teal-dark) 100%)'
            }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Interested in Our Products?
            </h2>
            <p className="text-lg mb-6 text-gray-100">
              Contact us for detailed catalogs, pricing, and custom requirements
            </p>
            <Button className="btn-gold text-lg px-8 py-5">
              Request Product Catalog
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
